import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useApplications = (getAuthHeaders, initialStatus = 'all') => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialStatus);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/deal-applications`, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const filteredApplications = useMemo(() => {
    if (applications.length === 0) return [];

    switch (activeTab) {
      case 'to-review':
        return applications.filter(app => app.status === 'Pending' || app.status === 'Under Review');
      case 'approved':
        return applications.filter(app => app.status === 'Approved');
      case 'declined':
        return applications.filter(app => app.status === 'Rejected' || app.status === 'Declined');
      case 'killed':
        return applications.filter(app => app.status === 'Killed');
      default:
        return applications;
    }
  }, [applications, activeTab]);

  const statusCounts = useMemo(() => ({
    all: applications.length,
    toReview: applications.filter(a => a.status === 'Pending' || a.status === 'Under Review').length,
    approved: applications.filter(a => a.status === 'Approved').length,
    declined: applications.filter(a => a.status === 'Rejected' || a.status === 'Declined').length,
    killed: applications.filter(a => a.status === 'Killed').length,
  }), [applications]);

  return {
    applications,
    filteredApplications,
    loading,
    activeTab,
    setActiveTab,
    statusCounts,
    refetch: fetchApplications,
  };
};

export const useApplicationActions = (getAuthHeaders, refetch) => {
  const changeStatus = useCallback(async (appId, newStatus, notes = '', sendEmail = true) => {
    try {
      await axios.put(
        `${API}/deal-applications/${appId}/change-status`,
        { status: newStatus, notes, send_email: sendEmail },
        { headers: getAuthHeaders() }
      );
      const emailMsg = sendEmail ? ' Email notification sent.' : '';
      toast.success(`Status changed to ${newStatus}.${emailMsg}`);
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  const quickStatusChange = useCallback(async (app, newStatus) => {
    if (newStatus === app.status) return false;
    
    try {
      await axios.patch(`${API}/deal-applications/${app.id}/status`, {
        status: newStatus,
        notes: `Status changed from ${app.status} to ${newStatus}`
      }, { headers: getAuthHeaders() });
      toast.success(`Status changed to ${newStatus}`);
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  const addNote = useCallback(async (appId, content) => {
    try {
      await axios.post(
        `${API}/deal-applications/${appId}/notes`,
        { content },
        { headers: getAuthHeaders() }
      );
      toast.success('Note added successfully');
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  const updateApplication = useCallback(async (appId, data) => {
    try {
      await axios.put(
        `${API}/deal-applications/${appId}`,
        data,
        { headers: getAuthHeaders() }
      );
      toast.success('Application updated successfully');
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  const reviewApplication = useCallback(async (appId, action, rejectionReason = '') => {
    try {
      if (action === 'approve') {
        await axios.put(
          `${API}/deal-applications/${appId}/review`,
          null,
          { headers: getAuthHeaders(), params: { status: 'Approved' } }
        );
        toast.success('Application approved');
      } else if (action === 'reject') {
        if (!rejectionReason) {
          toast.error('Please provide a rejection reason');
          return false;
        }
        await axios.put(
          `${API}/deal-applications/${appId}/review`,
          null,
          { headers: getAuthHeaders(), params: { status: 'Rejected', rejection_reason: rejectionReason } }
        );
        toast.success('Application rejected');
      }
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  const convertToDeal = useCallback(async (app, dealDetails) => {
    const { approved_amount, factor_rate, term_days, commission_rate } = dealDetails;

    if (!approved_amount || !factor_rate || !term_days || !commission_rate) {
      toast.error('Please fill in all deal details');
      return false;
    }

    try {
      const dealData = {
        deal_name: `${app.business_name} - Application #${app.id.substring(0, 8)}`,
        business_name: app.business_name,
        dba: app.dba,
        ein_tax_id: app.ein_tax_id,
        business_type: app.business_type,
        industry: app.industry,
        years_in_business: app.years_in_business,
        monthly_revenue: app.monthly_revenue,
        owner_name: app.owner_name,
        owner_ssn: app.owner_ssn,
        funding_amount: parseFloat(approved_amount),
        rate: parseFloat(factor_rate),
        term_days: parseFloat(term_days),
        commission_rate: parseFloat(commission_rate),
        commission_type: 'iso',
        application_id: app.id,
      };

      await axios.post(`${API}/deal-applications/${app.id}/convert-to-deal`, dealData, {
        headers: getAuthHeaders()
      });

      toast.success('Application converted to deal successfully');
      refetch();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to deal');
      return false;
    }
  }, [getAuthHeaders, refetch]);

  return {
    changeStatus,
    quickStatusChange,
    addNote,
    updateApplication,
    reviewApplication,
    convertToDeal,
  };
};

export default useApplications;
